import { h } from 'preact'
import { useState } from 'preact/hooks'

/**
 * UserBubble — renders a user message aligned to the right (like a chat app).
 * Props: { message, i18n }
 * message: { id, type:'user', content, attachments, avatarUrl, timestamp }
 */
export function UserBubble({ message, i18n }) {
  const time = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : ''

  return (
    <div class="message message-user mb-2">
      <div class="d-flex align-items-start flex-row-reverse">
        <div class="message-avatar ms-3 flex-shrink-0">
          <AvatarImg avatarUrl={message.avatarUrl} />
        </div>
        <div class="message-content" style="min-width:0;max-width:75%;">
          <div class="message-header d-flex align-items-center justify-content-end mb-2">
            <small class="text-muted">{time}</small>
          </div>
          <div class="message-text p-3 rounded">
            {message.content}
          </div>
          {message.attachments?.length > 0 && (
            <AttachmentList attachments={message.attachments} i18n={i18n} />
          )}
        </div>
      </div>
    </div>
  )
}

function AvatarImg({ avatarUrl }) {
  const [imgFailed, setImgFailed] = useState(false)
  const showImg = avatarUrl && !imgFailed

  return (
    <div
      class="rounded-circle d-flex align-items-center justify-content-center overflow-hidden bg-light"
      style="width:32px;height:32px;flex-shrink:0;"
    >
      {showImg
        ? <img
            src={avatarUrl}
            alt="User"
            width="32"
            height="32"
            style="width:32px;height:32px;object-fit:cover;display:block;"
            onError={() => setImgFailed(true)}
          />
        : <i class="bi bi-person-fill text-muted" />
      }
    </div>
  )
}

function AttachmentList({ attachments, i18n }) {
  return (
    <div class="d-flex flex-wrap gap-2 mt-2">
      {attachments.map((att, idx) => <AttachmentItem key={idx} att={att} i18n={i18n} />)}
    </div>
  )
}

function AttachmentItem({ att, i18n }) {
  const blobUrl = att.signed_id && att.filename
    ? `/storage/blobs/redirect/${att.signed_id}/${encodeURIComponent(att.filename)}`
    : null

  const wrap = (child) => blobUrl
    ? <a href={blobUrl} target="_blank" rel="noopener" class="text-decoration-none">{child}</a>
    : child

  if (att.type === 'image' && att.preview_url) {
    return wrap(
      <div class="position-relative">
        <img src={att.preview_url} class="rounded border" style="max-width:150px;max-height:150px;object-fit:cover;" />
        {att.filename && <div class="text-white small mt-1" style="text-shadow:1px 1px 2px rgba(0,0,0,.8)">{att.filename}</div>}
      </div>
    )
  }
  if (att.type === 'pdf') {
    return wrap(
      <div class="d-flex align-items-center bg-light border rounded p-2 text-danger">
        <i class="bi bi-file-pdf me-2"></i>
        <span>{att.filename || i18n?.t('ai.attachments.pdf_file') || 'PDF'}</span>
      </div>
    )
  }
  if (att.type === 'word') {
    return wrap(
      <div class="d-flex align-items-center bg-light border rounded p-2 text-primary">
        <i class="bi bi-file-word me-2"></i>
        <span>{att.filename || i18n?.t('ai.attachments.word_file') || 'Word'}</span>
      </div>
    )
  }
  return wrap(
    <div class="d-flex align-items-center bg-light border rounded p-2 text-secondary">
      <i class="bi bi-file-earmark me-2"></i>
      <span>{att.filename || i18n?.t('ai.attachments.file') || 'Fájl'}</span>
    </div>
  )
}
